import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { GitBranch, Move, ArrowRight, Plus, Trash2, Wand2, LayoutTemplate, Maximize2, Save, Download, ChevronUp, ChevronDown } from 'lucide-react';
import html2canvas from 'html2canvas';

const TEMPLATES = [
  {
    id: 'causal',
    name: 'Box-and-Arrow (Causal)',
    description: 'IV → Mediator → DV, with Moderator affecting the path',
    icon: '→'
  },
  {
    id: 'sequential',
    name: 'Sequential (Process)',
    description: 'Connects all constructs in a chain from first to last',
    icon: '⟹'
  },
  {
    id: 'hub',
    name: 'Hub (Centralized)',
    description: 'All Independent Variables point to all Dependent Variables',
    icon: '⊕'
  }
];

const C3_RelationshipMapping = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const canvasRef = useRef(null);
  const diagramWrapperRef = useRef(null);
  const isInitialized = useRef(false);
  
  const theories = dataC?.theoreticalFoundations?.filter(t => t.name?.trim()) || [];
  const theoryLinks = dataC?.theoryLinks || {};
  
  const getTheoriesForConstruct = (constructId) => {
    const linkedTheories = [];
    Object.keys(theoryLinks).forEach(theoryIndex => {
      const link = theoryLinks[theoryIndex];
      if (link?.linkedConstructs?.includes(constructId)) {
        const theory = theories[parseInt(theoryIndex)];
        if (theory) {
          linkedTheories.push({
            name: theory.name,
            shortName: theory.name.length > 15 ? theory.name.substring(0, 12) + '...' : theory.name
          });
        }
      }
    });
    return linkedTheories;
  };
  
  const CANVAS_WIDTH = 700;
  const DEFAULT_CANVAS_HEIGHT = 400;
  const MIN_CANVAS_HEIGHT = 300;
  const MAX_CANVAS_HEIGHT = 800;
  const HEIGHT_STEP = 100;
  const NODE_WIDTH = 150;
  const NODE_HEIGHT = 80;
  const LEGEND_HEIGHT = 40;
  const PADDING = 20;
  
  const [canvasHeight, setCanvasHeight] = useState(dataC?.diagramCanvasHeight || DEFAULT_CANVAS_HEIGHT);
  const [saveMessage, setSaveMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const createNodesFromConstructs = (constructs, height = canvasHeight) => {
    if (!constructs || constructs.length === 0) return [];
    const usableWidth = CANVAS_WIDTH - PADDING * 2;
    const usableHeight = height - PADDING * 2 - LEGEND_HEIGHT;
    const cols = Math.min(3, constructs.length);
    const rows = Math.ceil(constructs.length / cols);
    const cellWidth = usableWidth / cols;
    const cellHeight = usableHeight / rows;
    
    return constructs.map((c, i) => ({
      id: c.id,
      name: c.name || 'Unnamed',
      type: c.type || 'independent',
      x: PADDING + (i % cols) * cellWidth + (cellWidth - NODE_WIDTH) / 2,
      y: PADDING + Math.floor(i / cols) * cellHeight + (cellHeight - NODE_HEIGHT) / 2
    }));
  };
  
  const getInitialNodes = () => {
    // Check if we have valid saved diagram nodes with matching construct IDs
    if (dataC?.diagramNodes && dataC.diagramNodes.length > 0 && dataC?.constructs) {
      const constructMap = new Map(dataC.constructs.map(c => [c.id, c]));
      const nodesMatchConstructs = dataC.diagramNodes.every(n => constructMap.has(n.id));
      if (nodesMatchConstructs && dataC.diagramNodes.every(n => n.name)) {
        // Sync types from constructs to ensure they're up to date
        return dataC.diagramNodes.map(node => {
          const construct = constructMap.get(node.id);
          return {
            ...node,
            type: construct?.type || node.type,
            name: construct?.name || node.name
          };
        });
      }
    }
    // Otherwise create fresh nodes from constructs
    return createNodesFromConstructs(dataC?.constructs);
  };
  
  const [nodes, setNodes] = useState(getInitialNodes);
  const [connections, setConnections] = useState(() => {
    const saved = dataC?.diagramConnections || [];
    return saved.map(c => ({
      ...c,
      arrowDirection: c.arrowDirection || 'forward',
      lineStyle: c.lineStyle || 'solid'
    }));
  });
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Sync nodes when constructs change (e.g., user went back and modified)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      // Save initial nodes to context
      if (nodes.length > 0) {
        updateDataRaw({ 'wizardC.diagramNodes': nodes });
      }
      return;
    }
    
    // If constructs changed after initialization, sync or recreate nodes
    if (dataC?.constructs && dataC.constructs.length > 0) {
      const constructMap = new Map(dataC.constructs.map(c => [c.id, c]));
      const nodesMatchConstructs = nodes.length === dataC.constructs.length && 
        nodes.every(n => constructMap.has(n.id));
      
      if (!nodesMatchConstructs) {
        // Constructs were added/removed, recreate nodes
        const newNodes = createNodesFromConstructs(dataC.constructs);
        setNodes(newNodes);
        updateDataRaw({ 'wizardC.diagramNodes': newNodes });
      } else {
        // Sync types and names from constructs (user may have changed them)
        const syncedNodes = nodes.map(node => {
          const construct = constructMap.get(node.id);
          if (construct && (node.type !== construct.type || node.name !== construct.name)) {
            return { ...node, type: construct.type, name: construct.name };
          }
          return node;
        });
        
        const hasChanges = syncedNodes.some((n, i) => 
          n.type !== nodes[i].type || n.name !== nodes[i].name
        );
        
        if (hasChanges) {
          setNodes(syncedNodes);
          updateDataRaw({ 'wizardC.diagramNodes': syncedNodes });
        }
      }
    }
  }, [dataC?.constructs]);

  const handleMouseDown = (e, node) => {
    if (e.button === 0) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - node.x,
        y: e.clientY - rect.top - node.y
      });
      setDraggedNode(node.id);
    }
  };

  const handleMouseMove = (e) => {
    if (draggedNode) {
      const rect = canvasRef.current.getBoundingClientRect();
      const newX = Math.max(0, Math.min(e.clientX - rect.left - dragOffset.x, rect.width - NODE_WIDTH));
      const newY = Math.max(0, Math.min(e.clientY - rect.top - dragOffset.y, rect.height - NODE_HEIGHT));
      
      const updated = nodes.map(n => 
        n.id === draggedNode ? { ...n, x: newX, y: newY } : n
      );
      setNodes(updated);
    }
  };

  const handleMouseUp = () => {
    if (draggedNode) {
      updateDataRaw({ 'wizardC.diagramNodes': nodes });
      setDraggedNode(null);
      setIsDirty(true);
    }
  };

  const autofitNodes = useCallback(() => {
    if (nodes.length === 0) return;
    
    const usableWidth = CANVAS_WIDTH - PADDING * 2;
    const usableHeight = canvasHeight - PADDING * 2 - LEGEND_HEIGHT;
    const cols = Math.min(3, nodes.length);
    const rows = Math.ceil(nodes.length / cols);
    const cellWidth = usableWidth / cols;
    const cellHeight = usableHeight / rows;
    
    const updatedNodes = nodes.map((node, i) => ({
      ...node,
      x: PADDING + (i % cols) * cellWidth + (cellWidth - NODE_WIDTH) / 2,
      y: PADDING + Math.floor(i / cols) * cellHeight + (cellHeight - NODE_HEIGHT) / 2
    }));
    
    setNodes(updatedNodes);
    updateDataRaw({ 'wizardC.diagramNodes': updatedNodes });
  }, [nodes, canvasHeight, updateDataRaw]);

  const increaseCanvasHeight = () => {
    if (canvasHeight < MAX_CANVAS_HEIGHT) {
      const newHeight = Math.min(canvasHeight + HEIGHT_STEP, MAX_CANVAS_HEIGHT);
      setCanvasHeight(newHeight);
      updateDataRaw({ 'wizardC.diagramCanvasHeight': newHeight });
    }
  };

  const decreaseCanvasHeight = () => {
    if (canvasHeight > MIN_CANVAS_HEIGHT) {
      const newHeight = Math.max(canvasHeight - HEIGHT_STEP, MIN_CANVAS_HEIGHT);
      setCanvasHeight(newHeight);
      updateDataRaw({ 'wizardC.diagramCanvasHeight': newHeight });
    }
  };

  const saveArrangement = () => {
    updateDataRaw({ 
      'wizardC.diagramNodes': nodes,
      'wizardC.diagramConnections': connections,
      'wizardC.diagramCanvasHeight': canvasHeight
    });
    setIsDirty(false);
    setSaveMessage('Arrangement saved!');
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const exportDiagramAsPNG = async () => {
    if (!diagramWrapperRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(diagramWrapperRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = 'conceptual-framework-diagram.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting diagram:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const startConnection = (nodeId) => {
    if (connectingFrom === null) {
      setConnectingFrom(nodeId);
    } else if (connectingFrom !== nodeId) {
      const exists = connections.some(c => 
        (c.from === connectingFrom && c.to === nodeId) ||
        (c.from === nodeId && c.to === connectingFrom)
      );
      if (!exists) {
        const newConnection = {
          id: Date.now(),
          from: connectingFrom,
          to: nodeId,
          type: 'direct',
          label: '',
          arrowDirection: 'forward',
          lineStyle: 'solid'
        };
        const updated = [...connections, newConnection];
        setConnections(updated);
        updateDataRaw({ 'wizardC.diagramConnections': updated });
        setIsDirty(true);
      }
      setConnectingFrom(null);
    } else {
      setConnectingFrom(null);
    }
  };

  const updateConnectionProperty = (connId, property, value) => {
    const updated = connections.map(c => 
      c.id === connId ? { ...c, [property]: value } : c
    );
    setConnections(updated);
    updateDataRaw({ 'wizardC.diagramConnections': updated });
    setIsDirty(true);
  };

  const updateConnectionType = (connId, type) => updateConnectionProperty(connId, 'type', type);
  const updateConnectionLabel = (connId, label) => updateConnectionProperty(connId, 'label', label.substring(0, 12));

  const deleteConnection = (connId) => {
    const updated = connections.filter(c => c.id !== connId);
    setConnections(updated);
    updateDataRaw({ 'wizardC.diagramConnections': updated });
    setSelectedConnection(null);
    setIsDirty(true);
  };

  const applyTemplate = (templateId) => {
    if (nodes.length < 2) {
      setShowTemplateModal(false);
      return;
    }

    let newConnections = [];
    let ivs = nodes.filter(n => n.type === 'independent');
    let dvs = nodes.filter(n => n.type === 'dependent');
    const mediators = nodes.filter(n => n.type === 'mediator');
    const moderators = nodes.filter(n => n.type === 'moderator');
    const controls = nodes.filter(n => n.type === 'control');

    // Fallback for hub/causal: if no DVs exist, treat last construct(s) as DV
    if ((templateId === 'hub' || templateId === 'causal') && dvs.length === 0 && ivs.length >= 2) {
      // Move last IV to be a DV
      const lastIV = ivs[ivs.length - 1];
      dvs = [{ ...lastIV, type: 'dependent' }];
      ivs = ivs.slice(0, -1);
    }

    const addConnection = (fromId, toId, type = 'direct', label = '') => {
      const exists = newConnections.some(c => 
        (c.from === fromId && c.to === toId) ||
        (c.from === toId && c.to === fromId)
      );
      if (!exists && fromId !== toId) {
        newConnections.push({
          id: Date.now() + Math.random(),
          from: fromId,
          to: toId,
          type,
          label,
          arrowDirection: 'forward',
          lineStyle: 'solid'
        });
      }
    };

    if (templateId === 'causal') {
      if (mediators.length > 0) {
        ivs.forEach(iv => {
          mediators.forEach(med => addConnection(iv.id, med.id, 'direct'));
        });
        mediators.forEach(med => {
          dvs.forEach(dv => addConnection(med.id, dv.id, 'direct'));
        });
      } else {
        ivs.forEach(iv => {
          dvs.forEach(dv => addConnection(iv.id, dv.id, 'direct'));
        });
      }
      moderators.forEach(mod => {
        if (mediators.length > 0) {
          mediators.forEach(med => addConnection(mod.id, med.id, 'moderated'));
        } else {
          dvs.forEach(dv => addConnection(mod.id, dv.id, 'moderated'));
        }
      });
      // Control variables connect to DVs
      controls.forEach(ctrl => {
        dvs.forEach(dv => addConnection(ctrl.id, dv.id, 'direct', 'controlled'));
      });
      const positionedNodes = positionNodesForCausal(nodes, ivs, mediators, dvs, moderators, controls);
      setNodes(positionedNodes);
      updateDataRaw({ 'wizardC.diagramNodes': positionedNodes });
    } else if (templateId === 'sequential') {
      for (let i = 0; i < nodes.length - 1; i++) {
        addConnection(nodes[i].id, nodes[i + 1].id, 'direct');
      }
      const positionedNodes = positionNodesSequential(nodes);
      setNodes(positionedNodes);
      updateDataRaw({ 'wizardC.diagramNodes': positionedNodes });
    } else if (templateId === 'hub') {
      ivs.forEach(iv => {
        dvs.forEach(dv => addConnection(iv.id, dv.id, 'direct'));
      });
      const positionedNodes = positionNodesHub(nodes, ivs, dvs);
      setNodes(positionedNodes);
      updateDataRaw({ 'wizardC.diagramNodes': positionedNodes });
    }

    setConnections(newConnections);
    updateDataRaw({ 'wizardC.diagramConnections': newConnections });
    setShowTemplateModal(false);
  };

  const positionNodesForCausal = (allNodes, ivs, mediators, dvs, moderators, controls = []) => {
    const canvasWidth = 650;
    const canvasHeight = 350;
    let positioned = [...allNodes];
    
    const leftX = 50;
    const midX = canvasWidth / 2 - 75;
    const rightX = canvasWidth - 200;
    const centerY = canvasHeight / 2 - 30;

    ivs.forEach((iv, i) => {
      const idx = positioned.findIndex(n => n.id === iv.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: leftX, y: 60 + i * 100 };
      }
    });

    mediators.forEach((med, i) => {
      const idx = positioned.findIndex(n => n.id === med.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: midX, y: 60 + i * 100 };
      }
    });

    dvs.forEach((dv, i) => {
      const idx = positioned.findIndex(n => n.id === dv.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: rightX, y: 60 + i * 100 };
      }
    });

    moderators.forEach((mod, i) => {
      const idx = positioned.findIndex(n => n.id === mod.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: midX, y: centerY + 120 + i * 80 };
      }
    });

    // Position control variables below the DVs
    controls.forEach((ctrl, i) => {
      const idx = positioned.findIndex(n => n.id === ctrl.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: rightX - 50, y: centerY + 120 + i * 80 };
      }
    });

    return positioned;
  };

  const positionNodesSequential = (allNodes) => {
    const canvasWidth = 600;
    const nodeWidth = 180;
    const nodesPerRow = Math.max(1, Math.floor(canvasWidth / nodeWidth));
    const spacing = Math.min(180, (canvasWidth - 50) / Math.max(1, Math.min(allNodes.length, nodesPerRow)));
    const rowHeight = 100;
    
    return allNodes.map((n, i) => {
      const row = Math.floor(i / nodesPerRow);
      const col = i % nodesPerRow;
      return {
        ...n,
        x: 50 + col * spacing,
        y: 80 + row * rowHeight
      };
    });
  };

  const positionNodesHub = (allNodes, ivs, dvs) => {
    let positioned = [...allNodes];
    const leftX = 80;
    const rightX = 450;

    ivs.forEach((iv, i) => {
      const idx = positioned.findIndex(n => n.id === iv.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: leftX, y: 50 + i * 90 };
      }
    });

    dvs.forEach((dv, i) => {
      const idx = positioned.findIndex(n => n.id === dv.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: rightX, y: 50 + i * 90 };
      }
    });

    const others = allNodes.filter(n => n.type !== 'independent' && n.type !== 'dependent');
    others.forEach((o, i) => {
      const idx = positioned.findIndex(n => n.id === o.id);
      if (idx !== -1) {
        positioned[idx] = { ...positioned[idx], x: 250, y: 250 + i * 80 };
      }
    });

    return positioned;
  };

  const getNodeCenter = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    return { x: node.x + 75, y: node.y + 30 };
  };

  const getNodeDimensions = (nodeId) => {
    const el = canvasRef.current?.querySelector(`[data-node-id="${nodeId}"]`);
    return {
      w: el ? el.offsetWidth : 160,
      h: el ? el.offsetHeight : 60
    };
  };

  const getEdgePoint = (fromId, toId) => {
    const fromNode = nodes.find(n => n.id === fromId);
    const toNode = nodes.find(n => n.id === toId);
    if (!fromNode || !toNode) return { x: 0, y: 0 };

    const fromDim = getNodeDimensions(fromId);
    const toDim = getNodeDimensions(toId);

    const cx = fromNode.x + fromDim.w / 2;
    const cy = fromNode.y + fromDim.h / 2;
    const tx = toNode.x + toDim.w / 2;
    const ty = toNode.y + toDim.h / 2;

    const dx = tx - cx;
    const dy = ty - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return { x: cx, y: cy };

    const halfW = fromDim.w / 2;
    const halfH = fromDim.h / 2;
    const ndx = dx / dist;
    const ndy = dy / dist;
    const scaleX = Math.abs(ndx) > 0.001 ? halfW / Math.abs(ndx) : Infinity;
    const scaleY = Math.abs(ndy) > 0.001 ? halfH / Math.abs(ndy) : Infinity;
    const edgeDist = Math.min(scaleX, scaleY, dist / 2);
    return { x: cx + ndx * edgeDist, y: cy + ndy * edgeDist };
  };

  const getTypeColor = (type) => {
    const colors = {
      independent: '#3b82f6',
      dependent: '#22c55e',
      mediator: '#f59e0b',
      moderator: '#8b5cf6',
      control: '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const handleContinue = () => {
    updateDataRaw({ 
      'wizardC.diagramNodes': nodes,
      'wizardC.diagramConnections': connections
    });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon relationship-icon">
          <GitBranch size={32} />
        </div>
        <div>
          <h2>Step 5: Map Relationships</h2>
          <p className="step-description">Drag constructs and connect them to show relationships</p>
        </div>
      </div>

      <div className="step-content">
        <div className="mapping-header-row">
          <div className="mapping-instructions">
            <div className="instruction-item">
              <Move size={16} />
              <span><strong>Drag</strong> boxes to position constructs</span>
            </div>
            <div className="instruction-item">
              <ArrowRight size={16} />
              <span><strong>Click</strong> the "+" on two boxes to connect them</span>
            </div>
          </div>
          <div className="mapping-actions">
            <div className="height-controls">
              <button 
                className="height-btn"
                onClick={decreaseCanvasHeight}
                disabled={canvasHeight <= MIN_CANVAS_HEIGHT}
                title="Decrease canvas height"
              >
                <ChevronUp size={16} />
              </button>
              <span className="height-label">{canvasHeight}px</span>
              <button 
                className="height-btn"
                onClick={increaseCanvasHeight}
                disabled={canvasHeight >= MAX_CANVAS_HEIGHT}
                title="Increase canvas height"
              >
                <ChevronDown size={16} />
              </button>
            </div>
            <button 
              className="autofit-btn"
              onClick={autofitNodes}
              disabled={nodes.length === 0}
              title="Arrange nodes to fit within canvas"
            >
              <Maximize2 size={18} />
              Autofit
            </button>
            <button 
              className={`save-btn ${isDirty ? 'has-changes' : ''}`}
              onClick={saveArrangement}
              disabled={!isDirty}
              title={isDirty ? 'Save arrangement' : 'No changes to save'}
            >
              <Save size={18} />
              Save
            </button>
            <button 
              className="export-btn"
              onClick={exportDiagramAsPNG}
              disabled={isExporting || nodes.length === 0}
              title="Download diagram as PNG"
            >
              <Download size={18} />
              {isExporting ? 'Exporting...' : 'Export PNG'}
            </button>
            <button 
              className="auto-generate-btn"
              onClick={() => setShowTemplateModal(true)}
              disabled={nodes.length < 2}
            >
              <Wand2 size={18} />
              Template
            </button>
          </div>
        </div>
        {saveMessage && <div className="save-message">{saveMessage}</div>}

        {showTemplateModal && (
          <div className="template-modal-overlay" onClick={() => setShowTemplateModal(false)}>
            <div className="template-modal" onClick={e => e.stopPropagation()}>
              <div className="template-modal-header">
                <LayoutTemplate size={24} />
                <h3>Choose a Relationship Template</h3>
              </div>
              <p className="template-modal-desc">
                Select a template to auto-generate relationships based on your construct types.
                This will replace any existing connections.
              </p>
              <div className="template-options">
                {TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    className="template-option"
                    onClick={() => applyTemplate(template.id)}
                  >
                    <span className="template-icon">{template.icon}</span>
                    <div className="template-info">
                      <strong>{template.name}</strong>
                      <span>{template.description}</span>
                    </div>
                  </button>
                ))}
              </div>
              <button className="template-cancel-btn" onClick={() => setShowTemplateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="diagram-container" ref={diagramWrapperRef}>
          <div 
            className="diagram-canvas"
            ref={canvasRef}
            style={{ height: `${canvasHeight}px` }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <svg className="connections-layer">
            <defs>
              <marker id="arrowhead" markerWidth="16" markerHeight="12" refX="15" refY="6" orient="auto" markerUnits="userSpaceOnUse">
                <polygon points="0 0, 16 6, 0 12" fill="#475569" />
              </marker>
              <marker id="arrowhead-selected" markerWidth="16" markerHeight="12" refX="15" refY="6" orient="auto" markerUnits="userSpaceOnUse">
                <polygon points="0 0, 16 6, 0 12" fill="#3b82f6" />
              </marker>
              <marker id="arrowhead-reverse" markerWidth="16" markerHeight="12" refX="1" refY="6" orient="auto" markerUnits="userSpaceOnUse">
                <polygon points="16 0, 0 6, 16 12" fill="#475569" />
              </marker>
              <marker id="arrowhead-reverse-selected" markerWidth="16" markerHeight="12" refX="1" refY="6" orient="auto" markerUnits="userSpaceOnUse">
                <polygon points="16 0, 0 6, 16 12" fill="#3b82f6" />
              </marker>
            </defs>
            {connections.map((conn) => {
              const fromEdge = getEdgePoint(conn.from, conn.to);
              const toEdge = getEdgePoint(conn.to, conn.from);
              const midX = (fromEdge.x + toEdge.x) / 2;
              const midY = (fromEdge.y + toEdge.y) / 2;
              const isSelected = selectedConnection === conn.id;
              const strokeColor = isSelected ? '#3b82f6' : '#64748b';
              const arrowDir = conn.arrowDirection || 'forward';
              const isDashed = conn.lineStyle === 'dashed';
              const displayLabel = conn.label ? conn.label.substring(0, 12) : '';
              const angleDeg = Math.atan2(toEdge.y - fromEdge.y, toEdge.x - fromEdge.x) * (180 / Math.PI);
              const flipLabel = angleDeg > 90 || angleDeg < -90;
              const labelAngle = flipLabel ? angleDeg + 180 : angleDeg;
              
              let markerStart = 'none';
              let markerEnd = 'none';
              
              if (arrowDir === 'forward' || arrowDir === 'both') {
                markerEnd = isSelected ? 'url(#arrowhead-selected)' : 'url(#arrowhead)';
              }
              if (arrowDir === 'backward' || arrowDir === 'both') {
                markerStart = isSelected ? 'url(#arrowhead-reverse-selected)' : 'url(#arrowhead-reverse)';
              }
              
              return (
                <g key={conn.id} onClick={() => setSelectedConnection(conn.id)} style={{ cursor: 'pointer' }}>
                  <line
                    x1={fromEdge.x}
                    y1={fromEdge.y}
                    x2={toEdge.x}
                    y2={toEdge.y}
                    stroke="transparent"
                    strokeWidth={14}
                  />
                  <line
                    x1={fromEdge.x}
                    y1={fromEdge.y}
                    x2={toEdge.x}
                    y2={toEdge.y}
                    stroke={strokeColor}
                    strokeWidth={isSelected ? 3 : 2}
                    strokeDasharray={isDashed ? '8,4' : 'none'}
                    markerEnd={markerEnd}
                    markerStart={markerStart}
                  />
                  {displayLabel && (
                    <g transform={`translate(${midX}, ${midY}) rotate(${labelAngle})`}>
                      <rect
                        x={-(displayLabel.length * 3.5) - 4}
                        y={-20}
                        width={(displayLabel.length * 7) + 8}
                        height={18}
                        rx={3}
                        ry={3}
                        fill="white"
                        stroke="#cbd5e1"
                        strokeWidth={1}
                      />
                      <text x={0} y={-8} textAnchor="middle" fontSize="11" fontWeight="500" fill="#000000">
                        {displayLabel}
                      </text>
                    </g>
                  )}
                  <g transform={`translate(${midX}, ${midY}) rotate(${labelAngle})`}>
                    <text x={0} y={displayLabel ? 16 : 5} textAnchor="middle" fontSize="10" fill="#94a3b8">
                      {conn.type === 'direct' ? '+' : conn.type === 'inverse' ? '−' : '~'}
                    </text>
                  </g>
                </g>
              );
            })}
          </svg>

          {nodes.length === 0 && (
            <div className="empty-canvas-message">
              <p>No constructs found. Please go back to Step 1 and add at least 2 constructs.</p>
            </div>
          )}

            {nodes.map((node) => {
              const linkedTheories = getTheoriesForConstruct(node.id);
              return (
                <div
                  key={node.id}
                  className={`diagram-node type-${node.type} ${connectingFrom === node.id ? 'connecting' : ''} ${linkedTheories.length > 0 ? 'has-theories' : ''}`}
                  data-node-id={node.id}
                  style={{ left: node.x, top: node.y, borderColor: getTypeColor(node.type) }}
                  onMouseDown={(e) => handleMouseDown(e, node)}
                >
                  <div className="node-header">
                    <div className="node-type-indicator" style={{ background: getTypeColor(node.type) }}>
                      {node.type.charAt(0).toUpperCase()}
                    </div>
                    <span className="node-label">{node.name}</span>
                    <button 
                      className="connect-btn"
                      onClick={(e) => { e.stopPropagation(); startConnection(node.id); }}
                      title="Click to connect"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {linkedTheories.length > 0 && (
                    <div className="node-theories">
                      {linkedTheories.map((theory, idx) => (
                        <div key={idx} className="theory-name-full">
                          {theory.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="diagram-legend">
              <span className="diagram-legend-item"><span className="diagram-legend-dot" style={{ background: '#3b82f6' }}></span>IV</span>
              <span className="diagram-legend-item"><span className="diagram-legend-dot" style={{ background: '#22c55e' }}></span>DV</span>
              <span className="diagram-legend-item"><span className="diagram-legend-dot" style={{ background: '#f59e0b' }}></span>Med</span>
              <span className="diagram-legend-item"><span className="diagram-legend-dot" style={{ background: '#8b5cf6' }}></span>Mod</span>
              <span className="diagram-legend-item"><span className="diagram-legend-dot" style={{ background: '#6b7280' }}></span>Ctrl</span>
            </div>
          </div>
        </div>

        {selectedConnection && (() => {
          const conn = connections.find(c => c.id === selectedConnection);
          if (!conn) return null;
          const fromNode = nodes.find(n => n.id === conn.from);
          const toNode = nodes.find(n => n.id === conn.to);
          return (
            <div className="connection-editor">
              <div className="editor-header">
                <h4>Edit Connection</h4>
                <span className="editor-connection-info">
                  {fromNode?.name || '?'} → {toNode?.name || '?'}
                </span>
              </div>
              <div className="editor-grid">
                <div className="editor-row">
                  <label>Relationship Type:</label>
                  <select
                    value={conn.type || 'direct'}
                    onChange={(e) => updateConnectionType(selectedConnection, e.target.value)}
                  >
                    <option value="direct">Direct (+)</option>
                    <option value="inverse">Inverse (−)</option>
                    <option value="moderated">Moderated (~)</option>
                  </select>
                </div>
                <div className="editor-row">
                  <label>Arrow Direction:</label>
                  <div className="arrow-direction-buttons">
                    <button
                      className={`arrow-dir-btn ${(conn.arrowDirection || 'forward') === 'forward' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'arrowDirection', 'forward')}
                      title="One-way: from → to"
                    >
                      →
                    </button>
                    <button
                      className={`arrow-dir-btn ${conn.arrowDirection === 'backward' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'arrowDirection', 'backward')}
                      title="One-way: to → from"
                    >
                      ←
                    </button>
                    <button
                      className={`arrow-dir-btn ${conn.arrowDirection === 'both' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'arrowDirection', 'both')}
                      title="Two-way (bidirectional)"
                    >
                      ↔
                    </button>
                    <button
                      className={`arrow-dir-btn ${conn.arrowDirection === 'none' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'arrowDirection', 'none')}
                      title="No arrows"
                    >
                      —
                    </button>
                  </div>
                </div>
                <div className="editor-row">
                  <label>Line Style:</label>
                  <div className="line-style-buttons">
                    <button
                      className={`line-style-btn ${(conn.lineStyle || 'solid') === 'solid' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'lineStyle', 'solid')}
                      title="Solid line"
                    >
                      <svg width="40" height="12"><line x1="2" y1="6" x2="38" y2="6" stroke="currentColor" strokeWidth="2" /></svg>
                      Solid
                    </button>
                    <button
                      className={`line-style-btn ${conn.lineStyle === 'dashed' ? 'active' : ''}`}
                      onClick={() => updateConnectionProperty(selectedConnection, 'lineStyle', 'dashed')}
                      title="Dashed line"
                    >
                      <svg width="40" height="12"><line x1="2" y1="6" x2="38" y2="6" stroke="currentColor" strokeWidth="2" strokeDasharray="6,3" /></svg>
                      Dashed
                    </button>
                  </div>
                </div>
                <div className="editor-row">
                  <label>Label (optional):</label>
                  <input
                    type="text"
                    value={conn.label || ''}
                    onChange={(e) => updateConnectionLabel(selectedConnection, e.target.value)}
                    placeholder="e.g., H1, mediates"
                    maxLength={12}
                  />
                </div>
              </div>
              <div className="editor-actions">
                <button className="close-editor-btn" onClick={() => setSelectedConnection(null)}>
                  Close
                </button>
                <button className="delete-connection-btn" onClick={() => deleteConnection(selectedConnection)}>
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={connections.length >= 1}
        continueLabel="Continue to Assumptions"
      />
    </div>
  );
};

export default C3_RelationshipMapping;
